<template>
  <div>
    <h1 class="mb-8 font-bold text-3xl">
      <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('prospeccoes')">Prospecção</inertia-link>
      <span class="text-indigo-400 font-medium">/</span> {{ title }}
    </h1>
    <div class="bg-white rounded-md shadow overflow-hidden max-w-4xl">
      <form @submit.prevent="submit">
        <div class="p-10 -mr-6 -mb-8 flex flex-wrap">
          <text-input v-if="editMode" v-model="form.dtProspeccao" disabled type="date" :error="form.errors.dtProspeccao" class="pr-6 pb-8 w-full lg:w-1/2" label="Dt. Prospecção" />
          <text-input v-if="editMode" v-model="form.hrProspeccao" disabled type="time" :error="form.errors.hrProspeccao" class="pr-6 pb-8 w-full lg:w-1/2" label="Hr. Prospecção" />
          <text-input v-model="form.nome" :error="form.errors.nome" :disabled="editMode" class="pr-6 pb-8 w-full lg:w-1/2" maxlength="45" label="Nome" />
          <text-input v-model="form.responsavel" :disabled="editMode" :error="form.errors.responsavel" class="pr-6 pb-8 w-full lg:w-1/2" maxlength="45" label="Responsável" />
          <select-input v-model="form.status" :disabled="!showButton" :error="form.errors.status" class="pr-6 pb-8 w-full lg:w-1/2" label="Situação">
            <option v-for="s in Object.keys(status)" :key="s" :value="s">{{ status[s] }}</option>
          </select-input>
          <select-input v-model="form.reacao" :disabled="editMode" :error="form.errors.reacao" class="pr-6 pb-8 w-full lg:w-1/2" label="Situação">
            <option v-for="opt in Object.keys(reacoes)" :key="opt" :value="opt">{{ reacoes[opt] }}</option>
          </select-input>
          <text-input v-model="form.tel" :disabled="editMode" :mask="['(##) ####-####', '(##) #####-####']" type="mask" :error="form.errors.tel" class="pr-6 pb-8 w-full lg:w-1/2" label="Telefone" />
          <text-input v-model="form.cel" :disabled="editMode" :mask="['(##) ####-####', '(##) #####-####']" type="mask" :error="form.errors.cel" class="pr-6 pb-8 w-full lg:w-1/2" label="Celular" />
          <select-multi :key="form.idVendedor" v-model="form.idVendedor" :disabled="editMode" :options="vendedores" :error="form.errors.idVendedor" placeholder="pesquise vendedor" :reduce="row => row.idPessoa" track-by="razaoSocial" class="pr-6 pb-8 w-full lg:w-1/2" label="Vendedor" label-field="razaoSocial" />
          <select-input v-model="form.idClienteOrigem" :disabled="editMode" :error="form.errors.idClienteOrigem" class="pr-6 pb-8 w-full lg:w-1/2" label="Como encontrou nós">
            <option v-for="opt in origens" :key="opt.idClienteOrigem" :value="opt.idClienteOrigem">{{ opt.descricao }}</option>
          </select-input>
          <select-input v-model="form.idEmpresaAtividade" :disabled="editMode" :error="form.errors.idEmpresaAtividade" class="pr-6 pb-8 w-full lg:w-1/2" label="Segmento">
            <option v-for="opt in segmentos" :key="opt.idEmpresaAtividade" :value="opt.idEmpresaAtividade">{{ opt.descricao }}</option>
          </select-input>
          <select-multi :key="form.codCidade" v-model="form.codCidade" :disabled="editMode" :error="form.errors.codCidade" :custom-label="row => `${row.cidade} - ${row.uf}`" :internal-search="false" :options="cidades" placeholder="pesquise cidade" :reduce="row => row.codCidade" track-by="codCidade" class="pr-6 pb-8 w-full lg:w-1/2" label="Cidade" label-field="cidade" @search-change="asyncFindCidade" />
          <div class="pr-6 pb-8 w-full lg:w-1/2 flex items-center">
            <label class="inline-flex items-center mt-3"> <input v-model="form.solicitouOrcamento" :disabled="editMode" type="checkbox" class="form-checkbox h-5 w-5 text-gray-600" true-value="S" false-value="N" @input="changeOrcamento" /><span class="ml-2 text-gray-700">Solicitou Orçamento</span> </label>
            <text-input v-model="form.idVenda" :mask="'##########'" type="mask" :disabled="form.solicitouOrcamento == 'N' || editMode" :error="form.errors.idVenda" label="ID" />
          </div>
          <text-input v-model="form.dtProximaVisita" :error="form.errors.dtProximaVisita" :disabled="!showButton" type="date" class="pr-6 pb-8 w-full lg:w-1/2" label="Próxima visita" />
          <select-multi :value="concorrentes.find(c => c.idSistemaConcorrente == form.idSistemaConcorrente)" :disabled="editMode" :options="concorrentes" :error="form.errors.idSistemaConcorrente" placeholder="sistema" track-by="idSistemaConcorrente" la class="pr-6 pb-8 w-full lg:w-1/2" label="Usa algum sistema" label-field="descricao" @input="opt => (form.idSistemaConcorrente = (opt ? opt.idSistemaConcorrente : null))" />
          <textarea-input v-model="form.obs" :disabled="!showButton" class="pr-6 pb-8 w-full" label="Observações" />
        </div>

        <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex justify-end items-center space-x-2">
          <button v-if="editMode" type="button" title="Abrir rotas" @click="openGoogleMaps(form.coordenadas)"><rota-icon class="h-10" /></button>
          <div v-if="editMode" class="flex-1" />
          <loading-button v-if="showButton" :loading="form.processing" class="btn-indigo" type="submit"> {{ submitTextButton }}</loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'

import { status, reacoes } from '@/Utils/helpers'
import SelectInput from '../../Shared/SelectInput.vue'
import SelectMulti from '../../Shared/SelectMulti.vue'
import TextareaInput from '../../Shared/TextareaInput.vue'
import RotaIcon from '../../Shared/RotaIcon.vue'
import { debounce } from 'lodash'
import { DateTime } from 'luxon'
const BASE_URL = 'prospeccoes'
export default {
  name: 'CreateOrEdit',
  metaInfo() {
    return { title: this.title }
  },
  components: {
    LoadingButton,
    TextInput,
    SelectInput,
    SelectMulti,
    TextareaInput,
    RotaIcon,
  },
  layout: Layout,
  props: {
    model: {
      type: Object,
      default() {
        return {}
      },
    },
    vendedores: Array,
    origens: Array,
    segmentos: Array,
    cidades: Array,
    concorrentes: Array,
  },
  data() {
    return {
      status,
      reacoes,
      show: false,
      imgDataUrl: '',
      showButton: true,
      form: this.$inertia.form({
        idProspeccao: null,
        idVendedor: null,
        reacao: 0,
        solicitouOrcamento: 'N',
        dtProspeccao: null,
        hrProspeccao: null,
        nome: null,
        responsavel: null,
        tel: null,
        cel: null,
        obs: null,
        coordenadas: null,
        codCidade: null,
        status: 'A',
        idVenda: null,
        idClienteOrigem: null,
        idEmpresaAtividade: null,
        dtProximaVisita: null,
        idSistemaConcorrente: null,
      }),
    }
  },
  computed: {
    title() {
      return this.editMode ? 'Editar' : 'Criar'
    },
    editMode() {
      return this.model.idProspeccao ? true : false
    },
    submitTextButton() {
      return this.editMode ? 'Atualizar' : 'Criar'
    },
  },
  created() {
    if (this.editMode) {
      this.form = { ...this.form, ...this.model, codCidade: this.model.cidade ? this.model.cidade : null, idVendedor: this.model.vendedor, dtProspeccao: this.model.dtProspeccao }
      this.handleHideButton()
    } else {
      this.initGeolocation()
      const now = DateTime.now()
      this.form.dtProspeccao = now.toJSDate()
      this.form.hrProspeccao = now.toFormat('HH:mm')
    }

    this.asyncFindCidade = debounce(query => {
      this.$inertia.get(
        this.route(BASE_URL + '.create'),
        {
          cidade: query,
        },
        {
          preserveState: true,
          preserveScroll: true,
        },
      )
    }, 500)
  },
  remember: 'form',
  methods: {
    submit() {
      if (this.editMode) {
        this.update()
      } else {
        this.store()
      }
    },
    store() {
      this.form
        .transform(data => ({
          ...data,
          codCidade: data.codCidade?.codCidade ? data.codCidade.codCidade : null,
          idVendedor: data.idVendedor?.idPessoa ? data.idVendedor.idPessoa : null,
        }))
        .post(this.route(BASE_URL + '.store'))
    },
    update() {
      this.form
        .transform(data => ({
          ...data,
          codCidade: data.codCidade?.codCidade ? data.codCidade.codCidade : null,
          idVendedor: data.idVendedor?.idPessoa ? data.idVendedor.idPessoa : null,
        }))
        .put(this.route(BASE_URL + '.update', this.model.idProspeccao), { onSuccess: () => this.handleHideButton() })
    },
    destroy() {
      if (confirm('Você deseja mesmo apagar esse registro?')) {
        // this.$inertia.delete(this.route(BASE_URL+'.destroy', this.model.idEmpresaAtividade))
      }
    },
    handleHideButton() {
      if (this.form.status == 'C' || this.form.status == 'F') {
        this.showButton = false
      }
    },
    changeOrcamento() {
      if (this.form.solicitouOrcamento == 'S') {
        this.form.idVenda = null
      }
    },
    asyncFindCidade() {},
    initGeolocation() {
      if (navigator.geolocation) {
        // Call getCurrentPosition with success and failure callbacks
        navigator.geolocation.getCurrentPosition(this.success, this.fail, { maximumAge: 60000, timeout: 5000, enableHighAccuracy: true })
      } else {
        alert('Seu navegador não suporta geolocalização.')
      }
    },
    success(position) {
      this.form.coordenadas = `${position.coords.latitude},${position.coords.longitude}`
    },

    fail() {
      alert('Permita  a localização!')
    },
    openGoogleMaps(coordenadas) {
      window.open(`https://www.google.com/maps/place/${coordenadas}`, '_blank').focus()
    },
  },
}
</script>
